import type { ReactElement } from "react";
import { Suspense } from "react";
import { Route } from "react-router-dom";

import { ProtectedRoute } from "./ProtectedRoute";

import {
  getFeatureNavigationPermission,
  type FeatureMetadata,
  type NavigationPersona,
} from "@/features/feature.types";

const warnedRouteFeatures = new Set<string>();

function warnInvalidRouteFeature(featureId: string, reason: string) {
  const warningKey = `${featureId}:${reason}`;
  if (warnedRouteFeatures.has(warningKey)) return;
  warnedRouteFeatures.add(warningKey);
  console.error(`[routes] Skipping feature "${featureId}" because ${reason}.`);
}

function toChildRoutePath(path: string): string {
  const normalized = String(path ?? "").trim();
  if (!normalized) return "";
  return normalized.startsWith("/") ? normalized.slice(1) : normalized;
}

/**
 * Generates React Router <Route> elements from feature metadata.
 * - Live features only
 * - Wrapped with ProtectedRoute using feature.permissions.view
 * - Lazy-loaded via feature.route.element
 * - Suspense fallback included
 */
export function buildRoutesFromFeatures(
  features: readonly FeatureMetadata[],
  options?: { suspenseFallback?: ReactElement | null; persona?: NavigationPersona },
): ReactElement[] {
  const suspenseFallback = options?.suspenseFallback ?? null;
  const persona = options?.persona ?? "staff";

  return features
    .filter((feature) => feature.readiness === "live")
    .flatMap((feature) => {
      const RouteElement = feature.route?.element;
      const childPath = toChildRoutePath(feature.route.path);

      if (!childPath) {
        warnInvalidRouteFeature(feature.id, "route.path is empty");
        return [];
      }

      if (!RouteElement) {
        warnInvalidRouteFeature(feature.id, "route.element is undefined");
        return [];
      }

      return (
        <Route
          key={feature.id}
          path={childPath}
          element={
            <ProtectedRoute permission={getFeatureNavigationPermission(feature, persona)}>
              <Suspense fallback={suspenseFallback}>
                <RouteElement />
              </Suspense>
            </ProtectedRoute>
          }
        />
      );
    });
}
